import { format, UTCDate } from '@/utils/dates';
import { getIsoStatusMessage } from '@/utils/get-iso-status-message';
import { PaymentStatus, TransactionEventStatus, TransactionEventType } from '@seapoint/server/enums';
import { CircleCheck, Clock, TriangleAlert } from 'lucide-react';
import { Inline } from '../../../../../design-system/inline/inline';
import { Stack } from '../../../../../design-system/stack/stack';

/** Minimal event data needed for timeline display */
export type TimelineEvent = {
  id: string;
  type: TransactionEventType;
  occurredAt: string | Date;
  title: string;
  description?: string | null;
  status: TransactionEventStatus;
  isoStatusCode?: string | null;
};

type PaymentTimelineProps = {
  currentStatus: PaymentStatus;
  events?: TimelineEvent[];
};

type TimelineStep = {
  id: string;
  icon: React.ReactNode;
  title: string;
  timestamp?: string;
  additionalInfo?: string;
};

type TimelineStepProps = TimelineStep & {
  isLast: boolean;
};

const TimelineStepComponent = ({ icon, title, timestamp, additionalInfo, isLast }: TimelineStepProps) => (
  <div className="flex gap-2.5">
    {/* Icon column with dotted connector */}
    <div className="flex shrink-0 flex-col items-center">
      <div className="shrink-0">{icon}</div>
      {!isLast && <div className="mt-1 h-full w-px border-l border-dashed border-border" />}
    </div>
    {/* Content column */}
    <Stack space={0.5} className={!isLast ? 'pb-3' : ''}>
      <p className="text-sm leading-5 text-black">{title}</p>
      {(timestamp || additionalInfo) && (
        <Inline space={1} className="text-xs text-muted-foreground">
          {timestamp && <span>{timestamp}</span>}
          {timestamp && additionalInfo && <span className="size-[3px] rounded-full bg-muted-foreground" />}
          {additionalInfo && <span>{additionalInfo}</span>}
        </Inline>
      )}
    </Stack>
  </div>
);

const formatEventTimestamp = (timestamp: string | Date): string => {
  const date = typeof timestamp === 'string' ? new UTCDate(timestamp) : new UTCDate(timestamp);
  return format(date, "MMM d 'at' HH:mm");
};

const getEventTimestamp = (event: TimelineEvent | undefined): string | undefined => {
  if (!event) return undefined;
  return formatEventTimestamp(event.occurredAt);
};

const DEFAULT_FAILURE_MESSAGE = 'Please contact your bank for more information.';

const getFailureReason = (event: TimelineEvent | undefined): string => {
  // Try ISO status message first, then fall back to description, then default
  const isoMessage = getIsoStatusMessage(event?.isoStatusCode);
  return isoMessage ?? event?.description ?? DEFAULT_FAILURE_MESSAGE;
};

export const PaymentTimeline = ({ currentStatus, events = [] }: PaymentTimelineProps) => {
  console.log('[PaymentTimeline] Rendering with:', {
    currentStatus,
    eventsCount: events.length,
    eventTypes: events.map((e) => e.type),
  });

  const submittedEvent = events.find((e) => e.type === TransactionEventType.PAYMENT_SUBMITTED);
  const settledEvent = events.find((e) => e.type === TransactionEventType.TRANSACTION_SETTLED);
  const failedEvent = events.find((e) => e.type === TransactionEventType.TRANSACTION_FAILED);

  console.log('[PaymentTimeline] Found events:', {
    submittedEvent: !!submittedEvent,
    settledEvent: !!settledEvent,
    failedEvent: !!failedEvent,
  });

  const steps: TimelineStep[] = [];

  // First step: Payment submitted (always shown if we have the event)
  if (submittedEvent) {
    steps.push({
      id: 'submitted',
      icon: <CircleCheck size={16} className="text-[#3F9B21]" />,
      title: 'Payment submitted',
      timestamp: formatEventTimestamp(submittedEvent.occurredAt),
    });
  }

  // Second step: depends on current status
  if (currentStatus === PaymentStatus.SUCCEEDED) {
    // Completed: show settled event
    steps.push({
      id: 'settled',
      icon: <CircleCheck size={16} className="text-[#3F9B21]" />,
      title: 'Payment processed by your bank',
      timestamp: getEventTimestamp(settledEvent),
    });
  } else if (currentStatus === PaymentStatus.FAILED) {
    // Failed: show failure with reason
    steps.push({
      id: 'failed',
      icon: <TriangleAlert size={16} className="text-destructive" />,
      title: 'Payment failed',
      timestamp: getEventTimestamp(failedEvent),
      additionalInfo: getFailureReason(failedEvent),
    });
  } else if (currentStatus === PaymentStatus.PROCESSING || currentStatus === PaymentStatus.CREATED) {
    // Pending: show waiting state
    steps.push({
      id: 'pending',
      icon: <Clock size={16} className="text-black" />,
      title: 'Waiting for your bank to process payment',
    });
  }

  if (steps.length === 0) {
    return null;
  }

  return (
    <Stack space={0} className="pb-5">
      {steps.map((step, index) => (
        <TimelineStepComponent key={step.id} {...step} isLast={index === steps.length - 1} />
      ))}
    </Stack>
  );
};
